import { captureException } from '@sentry/react';
import InputInterface from '~/modules/GameEngine/Input/Interface';
import AubioStrategy from '~/modules/GameEngine/Input/MicStrategies/Aubio';
import events from '~/modules/GameEvents/GameEvents';
import userMediaService from '~/modules/UserMedia/userMediaService';

const micDebugEnabled = () =>
  (typeof window !== 'undefined' && window.localStorage?.getItem('AK_MIC_DEBUG') === '1') || false;

const micLog = (...args: any[]) => {
  if (!micDebugEnabled()) return;
};

const isSpecialDeviceId = (deviceId?: string) => deviceId === 'default' || deviceId === 'communications';

export class MicInput implements InputInterface {
  private stream: MediaStream | null = null;
  private context: AudioContext | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;

  private frequencies: number[] = [0, 0];
  private volumes: number[] = [0, 0];

  private startedMonitoring = false;

  // Para evitar starts solapados (InputManager te está reiniciando 2 veces seguidas)
  private startToken = 0;

  // Cache del “device real” que corresponde a default/communications (cuando se puede)
  private resolvedDefaultDeviceId: string | null = null;
  private resolvedCommunicationsDeviceId: string | null = null;

  constructor(private channels = 2) { }

  /**
   * Warm-up/resolución:
   * - En Chrome/Edge, pedir exact:"default" / "communications" puede dar comportamientos raros.
   * - Mejor: abrir un stream corto SIN exact, leer track.getSettings().deviceId (real),
   *   cerrar, y luego pedir el stream “de verdad” con exact del id real.
   */
  private resolveSpecialToRealDeviceId = async (requestedId?: string): Promise<string | undefined> => {
    if (!requestedId) return undefined;

    if (requestedId === 'default' && this.resolvedDefaultDeviceId) {
      return this.resolvedDefaultDeviceId;
    }
    if (requestedId === 'communications' && this.resolvedCommunicationsDeviceId) {
      return this.resolvedCommunicationsDeviceId;
    }

    // Intento de warm-up: pedir audio sin exact para que el navegador “asiente” el dispositivo
    // y nos devuelva un deviceId real en settings.
    try {
      const warmupConstraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
        video: false,
      };

      micLog('resolveSpecialToRealDeviceId - warmup getUserMedia...', { requestedId, warmupConstraints });

      const warmupStream = await userMediaService.getUserMedia(warmupConstraints);
      const track = warmupStream.getAudioTracks()[0];
      const settings = track?.getSettings?.() ?? {};
      const realId = (settings as any)?.deviceId as string | undefined;

      warmupStream.getTracks().forEach((t) => t.stop());

      if (realId && realId !== 'default' && realId !== 'communications') {
        if (requestedId === 'default') this.resolvedDefaultDeviceId = realId;
        if (requestedId === 'communications') this.resolvedCommunicationsDeviceId = realId;

        micLog('resolveSpecialToRealDeviceId - resolved', { requestedId, realId });
        return realId;
      }

      micLog('resolveSpecialToRealDeviceId - could not resolve (settings.deviceId not real)', {
        requestedId,
        settings,
      });
    } catch (e: any) {
      micLog('resolveSpecialToRealDeviceId - warmup failed', { requestedId, name: e?.name, message: e?.message });
    }

    // Si no se pudo resolver, devolvemos el mismo (pero NO lo pondremos como exact)
    return requestedId;
  };

  public startMonitoring = async (deviceId?: string) => {
    // Si ya hay uno en marcha, no arrancamos otro
    if (this.startedMonitoring) return;
    this.startedMonitoring = true;

    const token = ++this.startToken;

    try {
      // 1) Resolver default/communications a un deviceId real (si se puede)
      const resolvedId = isSpecialDeviceId(deviceId) ? await this.resolveSpecialToRealDeviceId(deviceId) : deviceId;

      // Si durante el resolve alguien llamó a stop/start, abortamos
      if (token !== this.startToken) return;

      // 2) Constraints “suaves”: NO forzar channelCountMode explícito (puede liar al device default)
      //    - Si tenemos resolvedId real: usamos exact
      //    - Si sigue siendo "default"/"communications": NO usamos exact (dejamos al navegador decidir)
      const useExact =
        resolvedId &&
        !isSpecialDeviceId(resolvedId) && // si es un id real, ok
        typeof resolvedId === 'string';

      const requestedConstraints: MediaStreamConstraints = {
        audio: {
          ...(useExact ? { deviceId: { exact: resolvedId } } : {}),
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
        video: false,
      };

      micLog('MicInput.startMonitoring - requesting stream...', { deviceId, resolvedId, requestedConstraints });

      this.stream = await userMediaService.getUserMedia(requestedConstraints);

      // Si durante getUserMedia alguien llamó a stop/start, abortamos limpiando
      if (token !== this.startToken) {
        this.stream?.getTracks().forEach((t) => t.stop());
        this.stream = null;
        return;
      }

      const track = this.stream.getAudioTracks()[0];
      const settings = track?.getSettings?.() ?? {};
      const constraints = track?.getConstraints?.() ?? {};

      // Cachear deviceId real si lo tenemos
      const trackDeviceId = (settings as any)?.deviceId as string | undefined;
      if (deviceId === 'default' && trackDeviceId && trackDeviceId !== 'default') this.resolvedDefaultDeviceId = trackDeviceId;
      if (deviceId === 'communications' && trackDeviceId && trackDeviceId !== 'communications')
        this.resolvedCommunicationsDeviceId = trackDeviceId;

      micLog('MicInput stream acquired:', {
        requestedDeviceId: deviceId,
        resolvedId,
        trackDeviceId,
        trackReadyState: track?.readyState,
        trackMuted: (track as any)?.muted,
        trackEnabled: track?.enabled,
        settings,
        constraints,
      });

      try {
        this.context = new AudioContext();
        micLog('AudioContext created:', { state: this.context.state, sampleRate: this.context.sampleRate });

        if (this.context.state === 'suspended') {
          await this.context.resume();
          micLog('AudioContext resumed:', { state: this.context.state });
        }

        const source = this.context.createMediaStreamSource(this.stream);

        // Detectar canales reales del stream
        const actualChannelCount = (settings as any)?.channelCount ?? (constraints as any)?.channelCount ?? 1;
        const isMono = Number(actualChannelCount) === 1;

        micLog('MicInput channel info:', { actualChannelCount, isMono, requestedChannels: this.channels });

        const analyserCount = isMono ? 1 : Math.min(this.channels, 2);

        const analysers = Array.from({ length: analyserCount }, () => {
          const analyser = this.context!.createAnalyser();
          analyser.fftSize = 2048;
          analyser.minDecibels = -100;
          return analyser;
        });

        if (isMono || analyserCount === 1) {
          source.connect(analysers[0]);
        } else {
          const splitter = this.context.createChannelSplitter(2);
          source.connect(splitter);
          analysers.forEach((analyser, i) => splitter.connect(analyser, i));
        }

        const strategy = new AubioStrategy();
        await strategy.init(this.context, analysers[0].fftSize);
        micLog('AubioStrategy init OK');

        let tick = 0;

        // ✅ Intervalo correcto en ms
        const intervalMs = Math.max(10, (analysers[0].fftSize / this.context.sampleRate) * 1000);

        this.interval = setInterval(async () => {
          // Si se ha reiniciado mientras tanto, no proceses
          if (token !== this.startToken) return;

          const buffers = analysers.map((analyser) => new Float32Array(analyser.fftSize));
          analysers.forEach((analyser, i) => analyser.getFloatTimeDomainData(buffers[i]));

          const vols = buffers.map((data) => this.calculateVolume(data));
          const freqs = await Promise.all(buffers.map((data) => strategy.getFrequency(data)));

          const vol0 = vols[0] ?? 0;
          const freq0 = freqs[0] ?? 0;

          if (isMono) {
            this.volumes = [vol0, vol0];
            this.frequencies = [freq0, freq0];
          } else {
            this.volumes = vols.length >= 2 ? (vols as any) : [vol0, vol0];
            this.frequencies = freqs.length >= 2 ? (freqs as any) : [freq0, freq0];
          }

          tick++;
          if (micDebugEnabled() && tick % 40 === 0) {
            micLog('MicInput tick:', {
              vols: this.volumes,
              freqs: this.frequencies,
              intervalMs,
              deviceId,
              resolvedId,
              trackDeviceId: (track?.getSettings?.() as any)?.deviceId,
            });
          }
        }, intervalMs);

        events.micMonitoringStarted.dispatch();
      } catch (e) {
        captureException(e);
        console.error(e);
      }
    } catch (e: any) {
      this.startedMonitoring = false;

      captureException(e, { level: 'warning', extra: { message: 'MicInput.startMonitoring' } });
      micLog('MicInput.getUserMedia failed:', { name: e?.name, message: e?.message, constraint: e?.constraint });
      console.warn(e);
    }
  };

  public getFrequencies = () => this.frequencies;
  public getVolumes = () => this.volumes;
  public clearFrequencies = () => undefined;

  public stopMonitoring = async () => {
    if (!this.startedMonitoring) return;
    micLog('MicInput.stopMonitoring');

    this.startedMonitoring = false;

    // invalida cualquier start en curso
    this.startToken++;

    this.interval && clearInterval(this.interval);
    this.interval = null;

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;

    try {
      await this.context?.close();
    } catch (e) {
      console.log('MicInput.stopMonitoring error', e);
    }
    this.context = null;

    events.micMonitoringStopped.dispatch();
  };

  public getInputLag = () => 180;

  private calculateVolume(input: Float32Array) {
    let sum = 0.0;
    for (let i = 0; i < input.length; ++i) {
      sum += input[i] * input[i];
    }
    return Math.sqrt(sum / input.length);
  }

  public requestReadiness = () => Promise.resolve(true);
  public getStatus = () => 'ok' as const;
}

export default new MicInput();
